import { CheckoutContext } from "@/components/CheckoutForm/utils";
import { TRIBECA_ADDRESS } from "@/utils/constants";
import { Button, Checkbox, Textarea } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import React from "react";

interface CheckoutReviewOrderProps {
  ctx: CheckoutContext;
  onNext: () => void;
  onPrev: () => void;
  nextIsPending?: boolean;
}

export const CheckoutReviewOrder = ({
  ctx,
  onNext,
  onPrev,
  nextIsPending = false,
}: CheckoutReviewOrderProps) => {
  const { data, setData, shippingOptions } = ctx;
  const isMobile = useMediaQuery("(max-width: 767px)");
  const getShippingAddress = () => {
    if (
      data.shippingType === "pickup" ||
      data.shippingType === "oversized" ||
      data.shippingType === "multiple-calculated"
    ) {
      return TRIBECA_ADDRESS.address;
    } else if (data.shippingType === "shipToMyAddress") {
      // Use billing address for "ship to my address"
      return {
        line1: data.billingStreet,
        line2: data.billingApartment || "",
        city: data.billingCity,
        state: data.billingState,
        postal_code: data.billingZipcode,
        country: data.billingCountry || "US",
      };
    } else {
      // Use separate shipping address for "ship to different address"
      return {
        line1: data.shippingStreet,
        line2: data.shippingApartment || "",
        city: data.shippingCity,
        state: data.shippingState,
        postal_code: data.shippingZipcode,
        country: data.shippingCountry || "US",
      };
    }
  };
  const shippingAddress = getShippingAddress();
  // Phase B: is the box crossing the border? (Arranged types carry the real
  // destination in the delivery fields.)
  const isCaDestination =
    data.shippingType === "oversized" ||
    data.shippingType === "multiple-calculated"
      ? (data.deliveryCountry || "US") === "CA"
      : (shippingAddress as { country?: string }).country === "CA";
  const selectedShippingOption = shippingOptions.find(
    o => o.value === data.shippingOption
  );
  // 10-19: a custom-only cart (nothing shippable) selects the custom marker
  // rate; the review reads "Custom" like the cart-level switch does. The old
  // "Free Ground Shipping" label is gone — free shipping ended 10-04.
  const customRateSelected = selectedShippingOption?.label === "custom";
  const skipShipping = Boolean((ctx.cart as any)?.skipShipping);
  // F1 + oversized (2026-07-18): a selected Manhattan Messenger is a real,
  // priced method on both quote-arranged paths — the review label reflects
  // it; only the no-selection case is a quote request.
  const messengerSelected = shippingOptions.some(
    option =>
      option.value === data.shippingOption &&
      option.label === "manhattan-messenger"
  );
  // PIN #6 follow-through (2026-07-23): HI/AK destinations ride the internal
  // "oversized" type, and the CHIP got the approved Hawaii/Alaska copy on
  // 7/19 — but this review label still read "Oversized delivery requested"
  // for a Honolulu print. Same destination test as the chip.
  const isHiAkDelivery = (() => {
    // Phase B (2026-07-23): HI/AK is a US concept — a Canadian delivery
    // address must never read as Hawaii/Alaska.
    if ((data.deliveryCountry || "US") !== "US") return false;
    const state = String(data.deliveryState || "")
      .trim()
      .toUpperCase();
    if (["HI", "AK"].includes(state)) return true;
    const zip = parseInt(String(data.deliveryZipcode || "").substring(0, 5));
    if (!Number.isFinite(zip)) return false;
    return (zip >= 96700 && zip <= 96999) || (zip >= 99500 && zip <= 99999);
  })();
  const getShipToName = () => {
    if (data.shippingType === "pickup") {
      return "Tribeca Printworks";
    } else if (data.shippingType === "shipToOtherAddress") {
      return `${data.shippingFirstName || data.firstName} ${data.shippingLastName || data.lastName}`;
    } else {
      return `${data.firstName} ${data.lastName}`;
    }
  };
  return (
    <>
      <div>
        <div className="flex flex-col justify-between px-4 pb-7 pt-1 md:flex-row">
          <div>
            <h3 className="mb-6 text-xl font-bold leading-6">
              Additional Details
            </h3>
            <Checkbox
              label={
                <div className="flex flex-col gap-1">
                  White label
                  <span className="text-sm leading-none text-[#636363]">
                    (We won't include any company branding)
                  </span>
                </div>
              }
              checked={data.whiteLabel}
              onChange={event =>
                setData({ ...data, whiteLabel: event.currentTarget.checked })
              }
              className="mb-4 md:mb-6"
              styles={{
                body: {
                  display: "flex",
                  alignItems: "center",
                },
                input: {
                  cursor: "pointer",
                },
                label: {
                  fontSize: "16px",
                  cursor: "pointer",
                },
              }}
            />
            <Checkbox
              label="This is a gift"
              checked={data.gift}
              onChange={event =>
                setData({ ...data, gift: event.currentTarget.checked })
              }
              className="mb-4 md:mb-6"
              styles={{
                input: {
                  cursor: "pointer",
                },
                label: {
                  fontSize: "16px",
                  cursor: "pointer",
                },
              }}
            />
          </div>
          <div className="w-full md:max-w-[250px]">
            <p className="text-md mb-2 text-right font-[600] leading-[18px] text-[#636363]">
              Additional note (Optional)
            </p>
            <Textarea
              value={data.note}
              placeholder="Enter here"
              onChange={event =>
                setData({ ...data, note: event.currentTarget.value })
              }
              styles={{
                input: {
                  height: "70px",
                },
              }}
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 px-4 md:grid-cols-2">
        {/* Shipping Details Section — hidden for custom (skipped) shipping */}
        {!skipShipping && (
          <div>
            <h3 className="mb-4 text-lg font-bold leading-6 text-[#191919]">
              Shipping Details
            </h3>
            <div className="flex flex-col gap-2">
              {data.shippingType === "pickup" ? (
                <>
                  <div className="">
                    <span className="text-sm font-medium uppercase leading-[22px] text-gray-600">
                      Pickup Location:
                    </span>
                    <span className="ml-1 text-lg font-medium leading-[22px] text-[#191919]">
                      Tribeca Printworks
                    </span>
                  </div>
                  <div className="">
                    <span className="text-sm font-medium uppercase leading-[22px] text-gray-600">
                      Address:
                    </span>
                    <span className="ml-1 text-lg font-medium leading-[22px] text-[#191919]">
                      333 Hudson St, rm 201, New York, NY 10013
                    </span>
                  </div>
                </>
              ) : data.shippingType === "oversized" ||
                data.shippingType === "multiple-calculated" ? (
                <>
                  <div className="">
                    <span className="text-sm font-medium uppercase leading-[22px] text-gray-600">
                      Delivery:
                    </span>
                    <span className="ml-1 text-lg font-medium leading-[22px] text-[#191919]">
                      {(ctx.cart as any)?.skipShipping
                        ? "Custom"
                        : data.shippingType === "multiple-calculated"
                          ? messengerSelected
                            ? "Multiple Items — Manhattan Messenger"
                            : "Multiple Items, shipping quote requested"
                          : messengerSelected
                            ? "Manhattan Messenger"
                            : isHiAkDelivery
                              ? "Hawaii/Alaska — delivery arranged after order"
                              : "Oversized delivery requested"}
                    </span>
                  </div>
                  <div className="">
                    <span className="text-sm font-medium uppercase leading-[22px] text-gray-600">
                      Ship To:
                    </span>
                    <span className="ml-1 text-lg font-medium leading-[22px] text-[#191919]">
                      {data.deliveryFirstName} {data.deliveryLastName}
                      {data.deliveryCompanyName
                        ? `, ${data.deliveryCompanyName}`
                        : ""}
                    </span>
                  </div>
                  <div className="">
                    <span className="text-sm font-medium uppercase leading-[22px] text-gray-600">
                      Address:
                    </span>
                    <span className="ml-1 text-lg font-medium leading-[22px] text-[#191919]">
                      {data.deliveryStreet}
                      {data.deliveryApartment
                        ? `, ${data.deliveryApartment}`
                        : ""}
                    </span>
                  </div>
                  <div className="">
                    <span className="text-sm font-medium uppercase leading-[22px] text-gray-600">
                      City, State ZIP:
                    </span>
                    <span className="ml-1 text-lg font-medium leading-[22px] text-[#191919]">
                      {data.deliveryCity}, {data.deliveryState}{" "}
                      {data.deliveryZipcode}
                      {(data.deliveryCountry || "US") === "CA" ? ", Canada" : ""}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="">
                    <span className="text-sm font-medium uppercase leading-[22px] text-gray-600">
                      Ship To:
                    </span>
                    <span className="ml-1 text-lg font-medium leading-[22px] text-[#191919]">
                      {getShipToName()}
                    </span>
                  </div>
                  <div className="">
                    <span className="text-sm font-medium uppercase leading-[22px] text-gray-600">
                      Address:
                    </span>
                    <span className="ml-1 text-lg font-medium leading-[22px] text-[#191919]">
                      {shippingAddress.line1}
                      {shippingAddress.line2
                        ? `, ${shippingAddress.line2}`
                        : ""}
                    </span>
                  </div>
                  <div className="">
                    <span className="text-sm font-medium uppercase leading-[22px] text-gray-600">
                      City, State ZIP:
                    </span>
                    <span className="ml-1 text-lg font-medium leading-[22px] text-[#191919]">
                      {shippingAddress.city}, {shippingAddress.state}{" "}
                      {shippingAddress.postal_code}
                      {isCaDestination ? ", Canada" : ""}
                    </span>
                  </div>
                </>
              )}
            </div>
            {/* Phase B: DDU disclosure on review for Canadian destinations. */}
            {isCaDestination && (
              <p className="mt-2 text-sm leading-snug text-[#636363]">
                Canadian orders: no U.S. sales tax is charged. GST/HST and
                any customs/brokerage fees are billed to the recipient by
                the carrier at delivery.
              </p>
            )}
          </div>
        )}
        <div>
          <h3 className="mb-4 text-lg font-bold leading-6 text-[#191919]">
            Contact Information
          </h3>
          <div className="flex flex-col gap-2">
            <div className="">
              <span className="text-sm font-medium uppercase leading-[22px] text-gray-600">
                Customer Name:
              </span>
              <span className="ml-1 text-lg font-medium leading-[22px] text-[#191919]">
                {data.firstName} {data.lastName}
              </span>
            </div>
            {data.companyName && (
              <div className="">
                <span className="text-sm font-medium uppercase leading-[22px] text-gray-600">
                  Company:
                </span>
                <span className="ml-1 text-lg font-medium leading-[22px] text-[#191919]">
                  {data.companyName}
                </span>
              </div>
            )}
            <div className="">
              <span className="text-sm font-medium uppercase leading-[22px] text-gray-600">
                Email Address:
              </span>
              <span className="ml-1 text-lg font-medium leading-[22px] text-[#191919]">
                {data.email}
              </span>
            </div>
            <div className="">
              <span className="text-sm font-medium uppercase leading-[22px] text-gray-600">
                Phone Number:
              </span>
              <span className="ml-1 text-lg font-medium leading-[22px] text-[#191919]">
                {data.phoneNumber}
              </span>
            </div>
          </div>
        </div>
        {data.shippingType !== "oversized" &&
          data.shippingType !== "multiple-calculated" && (
            <div>
              <h3 className="mb-3 text-lg font-bold leading-6 text-[#191919]">
                Delivery Option
              </h3>
              <div className="flex flex-col gap-2">
                <div className="text-lg font-medium leading-tight text-[#191919]">
                  {(ctx.cart as any)?.skipShipping || customRateSelected
                    ? "Custom"
                    : data.shippingType === "pickup"
                      ? "Pick up in studio"
                      : (selectedShippingOption?.display_name ?? "—")}
                </div>
              </div>
            </div>
          )}
      </div>
      <div className="sticky bottom-0 mt-4 grid grid-cols-2 justify-between gap-3 rounded-b-xl border-t border-[#00000014] bg-white px-4 py-3 md:flex">
        <Button
          type="button"
          size="md"
          radius="xl"
          onClick={onPrev}
          w={isMobile ? "100%" : "120px"}
          h="40px"
          style={{ color: "#636363", backgroundColor: "#00000014" }}
        >
          Previous
        </Button>
        <Button
          type="button"
          size="md"
          radius="xl"
          onClick={onNext}
          w={isMobile ? "100%" : "200px"}
          h="40px"
          loading={nextIsPending}
          disabled={nextIsPending}
        >
          Save & Continue
        </Button>
      </div>
    </>
  );
};
